// Seller portal access card on the loan Parties tab — which portal accounts
// can see this loan's seller surfaces (distributions, statements, payment
// setup), and the manual link affordance. Mirrors BorrowerPortalAccessCard.
// Reads self-gate on Vendors.Read; linking gated Vendors.Manage / ADMIN. The
// picker searches the existing /portal/admin/accounts registry filtered to
// Seller accounts.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Button, Card, StatusBadge } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import { portalAccountsApi } from "@/api/portalAccounts";
import {
  partyLinkPickerKeys,
  useLinkSeller,
  useLoanSellerLinks,
} from "@/hooks/portfolioAdmin/useVendorReports";

export function SellerPortalAccessCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const { date } = useDateFormatter();
  const canRead = auth.hasPermission("Vendors.Read");
  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const linksQuery = useLoanSellerLinks(loanId, { enabled: canRead });
  const [linkOpen, setLinkOpen] = useState(false);

  if (!canRead) return null;

  const links = linksQuery.data ?? [];

  return (
    <Card data-testid="seller-portal-access-card">
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-2">
        <h3 className="font-display font-bold text-h3 m-0">
          Seller portal access
        </h3>
        {canManage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLinkOpen(true)}
            data-testid="seller-link-button"
          >
            <Plus size={14} aria-hidden /> Link seller portal account
          </Button>
        )}
      </div>
      <p className="text-caption text-gray-mid m-0 mb-space-2">
        Linked accounts see this loan&apos;s seller surfaces in the seller
        portal (distributions, statements, payment setup).
      </p>

      {linksQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">
          Loading linked accounts…
        </p>
      )}
      {linksQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load linked portal accounts. Refresh to retry.
        </p>
      )}
      {linksQuery.isSuccess &&
        (links.length === 0 ? (
          <p className="text-small text-gray-mid m-0">
            No seller portal account is linked to this loan yet.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
            {links.map((link) => (
              <li
                key={link.portalUserId}
                className="flex items-center justify-between gap-space-2 border-b border-border-light pb-space-1 text-small"
                data-testid={`seller-link-row-${link.portalUserId}`}
              >
                <span className="flex items-center gap-space-2 min-w-0">
                  <span className="font-medium whitespace-nowrap">
                    {link.fullName}
                  </span>
                  <span className="text-gray-mid truncate">{link.email}</span>
                  <StatusBadge tone="neutral">{link.source}</StatusBadge>
                </span>
                <span className="text-caption text-gray-mid whitespace-nowrap">
                  Linked {date(link.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        ))}

      {linkOpen && (
        <LinkSellerModal
          loanId={loanId}
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </Card>
  );
}

function LinkSellerModal({
  loanId,
  open,
  onClose,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [portalUserId, setPortalUserId] = useState("");
  const link = useLinkSeller(loanId);

  const accountsQuery = useQuery({
    queryKey: partyLinkPickerKeys.search("Seller", search),
    queryFn: () =>
      portalAccountsApi.list({ accountType: "Seller", search, limit: 20 }),
  });
  const accounts = accountsQuery.data?.items ?? [];

  const onSubmit = async () => {
    if (!portalUserId) {
      toast.error("Pick a portal account from the list.");
      return;
    }
    try {
      await link.mutateAsync(portalUserId);
      toast.success("Seller portal account linked.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Link seller portal account"
      description="Pick the seller's existing portal account. The link grants them this loan's seller surfaces."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={!portalUserId || link.isPending}
            data-testid="seller-link-submit"
          >
            Link account
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="seller-link-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Find portal account
          </span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="seller-link-search"
          />
        </label>

        <div
          className="max-h-48 overflow-y-auto border border-border-light rounded-md"
          data-testid="seller-link-list"
        >
          {accountsQuery.isLoading && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              Loading accounts…
            </p>
          )}
          {accountsQuery.isError && (
            <p className="text-caption text-status-error p-space-2 m-0">
              Could not load portal accounts.
            </p>
          )}
          {accountsQuery.isSuccess && accounts.length === 0 && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              No portal accounts match.
            </p>
          )}
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setPortalUserId(a.id)}
              className={`flex w-full items-center justify-between gap-space-2 px-space-2 py-space-1 text-left hover:bg-white-warm transition-colors ${
                portalUserId === a.id ? "bg-gold/10" : ""
              }`}
              data-testid={`seller-link-option-${a.id}`}
            >
              <span className="text-small min-w-0 truncate">
                {a.fullName}
                <span className="text-gray-mid"> · {a.email}</span>
              </span>
              <span className="text-caption text-gray-mid whitespace-nowrap">
                {a.accountType}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
